<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { NButton } from 'naive-ui'
import { useWikiPagesStore } from '../stores/wiki_pages'
import type { ThreadMessage } from '../stores/threads'
import { useWikiMarkdown, type CitationRef } from '../composables/useWikiMarkdown'
import ThreadCitedPages from './ThreadCitedPages.vue'

// One assistant answer in the Ask drawer: the brain mark, the serif
// answer body with inline `[n]` citation chips, the cited-page cards, a
// Copy action, and the weak-citation "add a source" nudge. `streaming`
// is true while this is the turn currently receiving deltas — it shows
// the pulsing caret and holds back the settled-answer affordances.
const props = defineProps<{
  message: ThreadMessage
  streaming: boolean
}>()

const emit = defineEmits<{
  (e: 'open-citation', path: string): void
  (e: 'add-source'): void
}>()

// How long the Copy button reads "Copied" before reverting.
const COPIED_RESET_MS = 2000

const wikiPages = useWikiPagesStore()
const { pages } = storeToRefs(wikiPages)
const wikiMarkdown = useWikiMarkdown()

const citations = computed(() => props.message.citations ?? [])

// Look up each citation in the catalog so the inline `[n]` marker's
// title attribute reads as a human name on hover. Falls back to the path
// when the catalog hasn't loaded yet or the cited page is gone.
function enrichWithTitles(refs: readonly CitationRef[]): CitationRef[] {
  return refs.map((c) => ({
    path: c.path,
    title: c.title || pages.value.find((p) => p.path === c.path)?.title || undefined,
  }))
}

const html = computed(() =>
  wikiMarkdown.renderWithCitations(props.message.content, 'index.md', enrichWithTitles(citations.value)),
)

const copied = ref(false)
let copiedTimer: number | undefined

async function copyAnswer(): Promise<void> {
  try {
    await navigator.clipboard.writeText(props.message.content)
    copied.value = true
    window.clearTimeout(copiedTimer)
    copiedTimer = window.setTimeout(() => (copied.value = false), COPIED_RESET_MS)
  } catch {
    copied.value = false
  }
}

onBeforeUnmount(() => window.clearTimeout(copiedTimer))
</script>

<template>
  <div class="flex gap-3.5" data-testid="thread-answer">
    <svg width="28" height="28" viewBox="0 0 26 26" aria-hidden="true" class="mt-0.5 flex-none">
      <circle cx="9" cy="13" r="6" fill="none" class="stroke-brand-primary" stroke-width="2" />
      <circle cx="17" cy="13" r="6" fill="none" class="stroke-factory" stroke-width="2" />
    </svg>
    <div class="min-w-0 flex-1">
      <!-- While streaming, the article and its last block go inline so
           the caret sits at the end of the text rather than on a new line. -->
      <article
        class="wiki-markdown font-serif text-[19px] leading-[1.65] text-ink-reading [&>:first-child]:mt-0 [&>:last-child]:mb-0"
        :class="streaming ? 'inline [&>:last-child]:inline' : 'block'"
        v-html="html"
      />
      <span
        v-if="streaming"
        class="ml-0.5 inline-block h-5 w-[9px] animate-pulse bg-brand-primary align-[-3px]"
        data-testid="thread-streaming-cursor"
        aria-hidden="true"
      />

      <template v-if="!streaming">
        <ThreadCitedPages
          v-if="citations.length"
          :citations="citations"
          class="mt-4"
          @open-citation="emit('open-citation', $event)"
        />

        <div class="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            class="min-h-9 cursor-pointer rounded-full border border-border-control bg-transparent px-3 text-[13px] text-ink-muted hover:text-ink"
            data-testid="thread-answer-copy"
            @click="copyAnswer"
          >
            {{ copied ? 'Copied' : 'Copy' }}
          </button>
        </div>

        <!-- Weak-citation nudge: a thinly-covered answer offers an
             in-flow path to close the gap. Only on the settled answer. -->
        <div
          v-if="message.weak_citation"
          class="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-factory/40 bg-factory/10 px-3 py-2 text-sm text-factory"
          data-testid="weak-citation-nudge"
        >
          <span class="flex-1">The wiki had limited coverage here.</span>
          <NButton
            size="small"
            type="warning"
            ghost
            class="min-h-9"
            data-testid="weak-citation-add-source"
            @click="emit('add-source')"
          >
            Add a source
          </NButton>
        </div>
      </template>
    </div>
  </div>
</template>
